/**
 * ProjectGenerationLauncher — Unified inline launcher for project Studio.
 *
 * - Two modes: Shoebox 3D (top-down) and FPS perspective (eye-level).
 * - All params are PRE-FILLED from the project's prompt-config so the user
 *   no longer has to re-type localisation / style / heure on every launch.
 *   The "Personnaliser" drawer reveals a small editor for the 4 most-changed
 *   fields (type bien, moment, vue, style).
 * - POSTs to the new project-scoped endpoints (`/api/projects/{id}/generation/...`)
 *   which check the per-profile `can_generate` permission so non-admins can
 *   launch generations as long as they have access to the project.
 * - FPS mode mounts the existing `FpsCameraPlanner` (no duplication).
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Wand2, Camera, Loader2, Sparkles, ChevronDown, ChevronRight, Settings2, History, RefreshCw, Layers, CheckSquare, Lock, Eye, Copy } from 'lucide-react';
import { HudCard, HudButton } from '../hud';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import FpsCameraPlanner from './FpsCameraPlanner';
import CorrespondanceBadge from './CorrespondanceBadge';
import CameraTestGrid from './CameraTestGrid';
import ABProjectionGrid from './ABProjectionGrid';
import DeterministicWhiteboxButton from './DeterministicWhiteboxButton';
import TypologyDefaultsWarning from '../generation/TypologyDefaultsWarning';
import { MaisonBatchLauncher } from './MaisonBatchLauncher';
import PromptConfigPanel, { OriginChip, ORIGIN_STYLE } from './PromptConfigPanel';
import { useRefsChanged, publishLauncherParams, useLauncherPatch, RESET_PARAM } from '../../lib/generationEvents';

const STAGE_TITLES = { whitebox: '1 · Maquette blanche', textured: '2 · Texturer', furnished: '3 · Meubler', final: '4 · Lumière & post-prod' };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { key: 'shoebox', label: 'Vue Shoebox',     sub: 'Plongée 3D depuis le plan',     icon: Wand2 },
  { key: 'fps',     label: 'Vue FPS',         sub: "Œil humain · perspective",      icon: Camera },
];

// Same option lists as on the admin page (kept short — full list still
// available on /generation if the user wants to fine-tune deeply).

const ProjectGenerationLauncher = ({ projectId, onJobLaunched, initialMode = 'shoebox', initialAdvanced = false }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [lots, setLots] = useState([]);
  const [loadingLots, setLoadingLots] = useState(true);
  const [selectedLotId, setSelectedLotId] = useState('');
  const [paramsPreview, setParamsPreview] = useState(null);   // resolved values shown in the readout
  const [promptMeta, setPromptMeta] = useState(null);
  const [customParams, setCustomParams] = useState(null);     // user override drawer
  const [launching, setLaunching] = useState(false);
  const [cameraTestId, setCameraTestId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('camera_test') || null; } catch { return null; }
  });
  const [abTestId, setAbTestId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('ab_test') || null; } catch { return null; }
  });
  const [lotHistory, setLotHistory] = useState([]); // last 6 renders for selected lot
  // Sprint 28C — Project typology for the defaults-warning banner. Pulled
  // once when the launcher mounts ; falls back to mergedParams.type_bien
  // (e.g. "Maison individuelle") which is normalized server-side anyway.
  const [projectTypeCode, setProjectTypeCode] = useState(null);

  // V11.5 — Multi-lot batch mode: lets the PM pick several lots at once
  // (e.g. "all Unique lots") and dispatch a single batch via the
  // /generation/create-batch endpoint, mirroring the admin Studio UX.
  const [batchMode, setBatchMode] = useState(false);
  // V15.11 — chantier C : parcours par étape, le reste dans un tiroir « Avancé »
  const [advancedOpen, setAdvancedOpen] = useState(initialAdvanced);
  const [stageAutoSet, setStageAutoSet] = useState(false);
  useEffect(() => { setStageAutoSet(false); }, [selectedLotId]);
  useEffect(() => {
    if (!promptMeta || stageAutoSet) return;
    setStageAutoSet(true);
    const next = !promptMeta.whitebox_reference_job_id ? 'whitebox'
      : !promptMeta.textured_reference_job_id ? 'textured'
      : !promptMeta.furnished_reference_job_id ? 'furnished' : 'final';
    setCustomParams((c) => ({ ...(c || {}), render_stage: next }));
  }, [promptMeta, stageAutoSet]);
  const [selectedLotIds, setSelectedLotIds] = useState(() => new Set());
  const [batchFilter, setBatchFilter] = useState('');
  const [lotGenCounts, setLotGenCounts] = useState({});  // {lot_id: count of completed shoebox jobs}
  const [lockStatus, setLockStatus] = useState({});      // {lot_id: {locked, source, walls_count, calibrated}}

  // V14.74 — Prompt preview (0 crédit) : shows the exact prompt + mask that
  // would be sent to the model for the selected lot.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // V14.93 — Scénario entre étapes : quand un rendu est validé/ancré dans la
  // galerie ou le wizard, on passe le lanceur sur l'étape suivante (PromptConfigPanel
  // se recharge de son côté) — plus besoin de rafraîchir la page.
  useRefsChanged(useCallback((d) => {
    if (d.lotId && d.lotId !== selectedLotId && !batchMode && lots.some((l) => l.lot_id === d.lotId)) {
      setSelectedLotId(d.lotId);
    }
    if (d.nextStage) {
      setCustomParams((c) => ({ ...(c || {}), render_stage: d.nextStage }));
      toast.success(`Étape suivante prête : ${STAGE_TITLES[d.nextStage] || d.nextStage}`, {
        description: 'Le lanceur affiche les paramètres appliqués à cette étape — ajustez puis lancez.',
      });
    }
  }, [selectedLotId, batchMode, lots]));

  // V15.11 — méta d'étape (références ancrées + Matériauthèque) chargée directement,
  // le PromptConfigPanel n'étant plus monté par défaut.
  const [metaNonce, setMetaNonce] = useState(0);
  useRefsChanged(useCallback(() => setMetaNonce((n) => n + 1), []));
  useEffect(() => {
    if (!projectId || !selectedLotId || batchMode) return undefined;
    let cancelled = false;
    axios.post(`${API}/projects/${projectId}/generation/preview-prompt`,
      { lot_id: selectedLotId, params: { render_stage: 'textured' } }, { withCredentials: true })
      .then(({ data: d }) => {
        if (cancelled) return;
        setPromptMeta({
          whitebox_reference_job_id: d.whitebox_reference_job_id, whitebox_reference_vue_angle: d.whitebox_reference_vue_angle,
          textured_reference_job_id: d.textured_reference_job_id, furnished_reference_job_id: d.furnished_reference_job_id,
          locked: d.locked, materials_brief_present: d.materials_brief_present,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, selectedLotId, batchMode, metaNonce]);

  // V14.94 — Partage des paramètres effectifs avec le wizard (lecture + patch).
  useEffect(() => {
    if (!paramsPreview) return;
    publishLauncherParams({ ...(paramsPreview || {}), ...(customParams || {}) });
  }, [paramsPreview, customParams]);
  useLauncherPatch(useCallback((patch) => {
    setCustomParams((c) => {
      const next = { ...(c || {}) };
      Object.entries(patch || {}).forEach(([k, v]) => {
        if (v === RESET_PARAM) {
          if (paramsPreview && paramsPreview[k] !== undefined) next[k] = paramsPreview[k]; else delete next[k];
        } else {
          next[k] = v;
        }
      });
      return next;
    });
  }, [paramsPreview]));


  // 1) Load lots for this project
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Sprint 28C — also fetch the project meta for the typology hint.
      try {
        const r = await axios.get(`${API}/projects/${projectId}`,
          { withCredentials: true });
        if (!cancelled) {
          const p = r.data || {};
          setProjectTypeCode(p.project_type_code
            || (p.is_maison ? 'house_single' : null));
        }
      } catch {
        /* silent — banner falls back to params.type_bien */
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingLots(true);
      try {
        const r = await axios.get(`${API}/projects/${projectId}/lots`, { withCredentials: true });
        if (cancelled) return;
        const list = r.data || [];
        setLots(list);
        // Priorité : lot de l'URL / dernier lot ouvert dans l'éditeur, sinon premier lot avec plan.
        let preferred = '';
        try {
          preferred = new URLSearchParams(window.location.search).get('lot_id') || localStorage.getItem(`pe_last_lot_${projectId}`) || '';
        } catch { /* no-op */ }
        const withPlan = list.find((l) => l.lot_id === preferred) || list.find((l) => l.selected_plan_file_id) || list[0];
        if (withPlan) setSelectedLotId(withPlan.lot_id);
      } catch {
        setLots([]);
      } finally {
        if (!cancelled) setLoadingLots(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // 2) Load the resolved prompt config (project-level overrides applied)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await axios.get(`${API}/projects/${projectId}/prompt-config`, { withCredentials: true });
        if (cancelled) return;
        const values = cfg.data?.values || {};
        setParamsPreview(values);
        setCustomParams((cur) => cur || {
          type_bien: values.type_bien || 'Appartement',
          etage_niveau: values.etage_niveau || 'Étage intermédiaire',
          moment_journee: values.moment_journee || 'Golden Hour',
          vue_angle: values.vue_angle || 'Plongée 45°',
          style_design: values.style_design || values.style || 'Contemporain',
          echelle_plan: values.echelle_plan || '1/100',
          orientation_nord: typeof values.orientation_nord === 'number' ? values.orientation_nord : 0,
          localisation: values.localisation || '',
        });
      } catch {
        setParamsPreview({});
        setCustomParams({
          type_bien: 'Appartement',
          moment_journee: 'Golden Hour',
          vue_angle: 'Plongée 45°',
          style: 'Contemporary',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const selectedLot = useMemo(() => lots.find((l) => l.lot_id === selectedLotId) || null, [lots, selectedLotId]);
  const planAttached = !!selectedLot?.selected_plan_file_id;

  // Load the last 6 renders for this lot (refreshed when lot changes or
  // after a launch via onJobLaunched). Helps the PM see if a new gen is
  // really useful or if the latest pass already covers the brief.
  useEffect(() => {
    if (!selectedLotId) {
      setLotHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/lots/${selectedLotId}/generations`, { withCredentials: true });
        if (cancelled) return;
        setLotHistory((r.data || []).slice(0, 6));
      } catch {
        if (!cancelled) setLotHistory([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLotId]);

  // 3) Project-wide map of completed Shoebox counts per lot — used in batch
  // mode to surface "déjà généré X×" pills + the "Non générés" quick-pick.
  // Refreshed whenever batch mode is opened or after a successful launch.
  const refreshLotGenCounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/generations`, { withCredentials: true });
      const list = r.data || [];
      const map = {};
      for (const j of list) {
        if (!j.lot_id) continue;
        if ((j.view_type || 'shoebox') !== 'shoebox') continue;
        if (j.status !== 'completed') continue;
        map[j.lot_id] = (map[j.lot_id] || 0) + 1;
      }
      setLotGenCounts(map);
    } catch {
      // best-effort: pills just won't render
    }
  }, [projectId]);

  // V14.71 — statut « 🔒 géométrie verrouillée » par lot (batch V2-aware)
  const refreshLockStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/generation/lots-lock-status`, { withCredentials: true });
      setLockStatus(r.data?.lots || {});
    } catch {
      // best-effort
    }
  }, [projectId]);

  useEffect(() => {
    if (!batchMode) return;
    refreshLotGenCounts();
    refreshLockStatus();
  }, [batchMode, refreshLotGenCounts, refreshLockStatus]);

  // Build final params payload by merging project config + custom overrides
  const buildParams = () => {
    const base = paramsPreview || {};
    return {
      ...base,
      ...(customParams || {}),
    };
  };

  const refreshHistory = async () => {
    if (!selectedLotId) return;
    try {
      const r = await axios.get(`${API}/lots/${selectedLotId}/generations`, { withCredentials: true });
      setLotHistory((r.data || []).slice(0, 6));
    } catch { /* keep current */ }
  };

  const openPromptPreview = async () => {
    const lotId = batchMode ? Array.from(selectedLotIds)[0] : selectedLotId;
    if (!lotId) return toast.error('Choisis d\'abord un lot.');
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/generation/preview-prompt`,
        { lot_id: lotId, params: buildParams() },
        { withCredentials: true },
      );
      setPreviewData(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Réessaie.';
      toast.error('Échec de la prévisualisation', { description: detail });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const launchCameraTest = async () => {
    if (!selectedLot?.selected_plan_file_id) return toast.error('Choisis un lot avec un plan retenu.');
    setLaunching(true);
    const toastId = toast.loading('Lancement du test caméra ×5…');
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/generation/camera-test`,
        { lot_id: selectedLot.lot_id, plan_file_id: selectedLot.selected_plan_file_id, params: { ...buildParams(), vue_angle: 'Aérienne isométrique 45°' }, count: 5 },
        { withCredentials: true },
      );
      setCameraTestId(data.camera_test_id);
      toast.success('5 rendus iso 45° lancés', { id: toastId, description: 'La grille se remplit au fil des rendus (~1-2 min).' });
      setTimeout(refreshHistory, 45000);
    } catch (e) {
      toast.error('Échec du test caméra', { id: toastId, description: e?.response?.data?.detail || e?.message });
    } finally {
      setLaunching(false);
    }
  };

  const launchShoebox = async () => {
    if (!selectedLot) return toast.error('Choisis d\'abord un lot.');
    if (!selectedLot.selected_plan_file_id) {
      return toast.error('Ce lot n\'a pas de plan retenu. Ouvre l\'onglet "Fichiers" pour en sélectionner un.');
    }
    setLaunching(true);
    const toastId = toast.loading('Création du job Shoebox…');
    try {
      const body = {
        lot_id: selectedLot.lot_id,
        plan_file_id: selectedLot.selected_plan_file_id,
        params: buildParams(),
        priority: 0,
      };
      const { data } = await axios.post(
        `${API}/projects/${projectId}/generation/create`,
        body,
        { withCredentials: true, timeout: 45000 },
      );
      toast.success('Job Shoebox lancé !', {
        id: toastId,
        description: `Apparaîtra dans la galerie (~30-60s). ID: ${data.job_id}`,
      });
      onJobLaunched?.(data);
      refreshHistory();
      setTimeout(refreshHistory, 8000);
      setTimeout(refreshHistory, 45000);
    } catch (e) {
      const status = e?.response?.status;
      const isGateway = status === 504 || status === 502 || status === 503 || e?.code === 'ECONNABORTED';
      const detail = isGateway
        ? 'Le serveur est saturé (génération en cours). Le job a peut-être été créé : vérifiez la galerie dans 30 s avant de relancer.'
        : (e?.response?.data?.detail || e?.message || 'Réessaie.');
      toast.error(status === 403 ? 'Permission refusée' : isGateway ? 'Serveur occupé (timeout)' : 'Échec du lancement', {
        id: toastId,
        description: detail,
        duration: isGateway ? 12000 : 6000,
        action: isGateway ? { label: 'Réessayer', onClick: () => launchShoebox() } : undefined,
      });
      if (isGateway) setTimeout(refreshHistory, 30000);
    } finally {
      setLaunching(false);
    }
  };

  // V11.5 — Multi-lot batch launcher. Dispatches every selected lot in a
  // single POST. The backend will skip lots without a plan (reported in
  // `skipped`) so the UI surfaces both counts in the toast.
  const launchBatch = async () => {
    const ids = Array.from(selectedLotIds);
    if (ids.length === 0) return toast.error('Sélectionne au moins un lot.');
    setLaunching(true);
    const toastId = toast.loading(`Création de ${ids.length} jobs Shoebox…`);
    try {
      const body = {
        lot_ids: ids,
        params: buildParams(),
        priority: 0,
      };
      const { data } = await axios.post(
        `${API}/projects/${projectId}/generation/create-batch`,
        body,
        { withCredentials: true, timeout: 120000 },
      );
      const created = (data.created || []).length;
      const skipped = (data.skipped || []).length;
      if (created > 0) {
        toast.success(`${created}/${ids.length} générations lancées`, {
          id: toastId,
          description: skipped
            ? `${skipped} lot(s) ignoré(s) (plan manquant ou invalide)`
            : 'Toutes en file d\'attente — apparaîtront dans la galerie.',
        });
        onJobLaunched?.(data);
        // Refresh counts a bit later so the "déjà généré" pills update
        setTimeout(refreshLotGenCounts, 10000);
        setTimeout(refreshLotGenCounts, 60000);
      } else {
        toast.error(`Aucune génération lancée (${skipped} ignorée(s))`, {
          id: toastId,
          description: 'Vérifie que tes lots ont un plan rattaché.',
        });
      }
    } catch (e) {
      const status = e?.response?.status;
      const isGateway = status === 504 || status === 502 || status === 503 || e?.code === 'ECONNABORTED';
      const detail = isGateway
        ? 'Le serveur est saturé. Les jobs ont peut-être été créés : vérifiez la galerie avant de relancer.'
        : (e?.response?.data?.detail || e?.message || 'Réessaie.');
      toast.error(status === 403 ? 'Permission refusée' : isGateway ? 'Serveur occupé (timeout)' : 'Échec du lancement batch', {
        id: toastId,
        description: detail,
        duration: isGateway ? 12000 : 6000,
      });
      if (isGateway) setTimeout(refreshLotGenCounts, 20000);
    } finally {
      setLaunching(false);
    }
  };

  // FPS confirm handler — same as the previous Studio integration, but routed
  // through the same project-scoped permission check.
  const handleFpsConfirm = async (payload) => {
    const toastId = toast.loading('Génération FPS en cours…', {
      description: `Lot ${payload.lot_id} · ${payload.fov_deg}° · angle ${payload.camera_position.angle}°`,
    });
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/generate-fps`,
        payload,
        { withCredentials: true },
      );
      toast.success('Job FPS lancé !', {
        id: toastId,
        description: `Apparaîtra dans la galerie (~30-60s). ID: ${data.job_id}`,
      });
      onJobLaunched?.(data);
      refreshHistory();
      setTimeout(refreshHistory, 8000);
      setTimeout(refreshHistory, 45000);
      setMode('shoebox'); // close FPS planner
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Réessaie.';
      toast.error(status === 403 ? 'Permission refusée' : 'Échec du lancement FPS', {
        id: toastId,
        description: detail,
      });
    }
  };

  // ----- FPS mode: just render the existing planner -----
  if (mode === 'fps') {
    return (
      <div className="space-y-3" data-testid="project-gen-launcher-fps">
        <ModeTabs mode={mode} setMode={setMode} />
        <FpsCameraPlanner
          projectId={projectId}
          initialLotId={selectedLotId || null}
          onConfirm={handleFpsConfirm}
          onClose={() => setMode('shoebox')}
        />
      </div>
    );
  }

  // ----- Shoebox mode -----
  // Centered card with a sane max-width so the form doesn't stretch absurdly
  // wide on big screens (the FPS planner below uses the full width instead).
  return (
    <div className="mx-auto w-full max-w-3xl">
    <HudCard variant="glass" bracket data-testid="project-gen-launcher-shoebox">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Lanceur de rendu</span>
          <p className="mt-1 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}>
            Générer un rendu
          </p>
          <p className="text-[12.5px] mt-1" style={{ color: 'var(--c-text-2)' }}>
            Tous les paramètres sont pré-remplis depuis la config du projet. Choisis un lot et lance.
          </p>
        </div>
        <Sparkles className="w-5 h-5 shrink-0" style={{ color: 'var(--c-gold)' }} />
      </div>

      <ModeTabs mode={mode} setMode={setMode} />

      {/* V11.5 — Multi-lot toggle (tiroir Avancé depuis V15.11 ; le batch projet vit dans « État géométrique »). */}
      {advancedOpen && (
      <div className="mt-2 flex items-center justify-between gap-3 rounded-md px-2.5 py-1.5"
           style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)' }}>
        <div className="flex items-center gap-2">
          <Layers className="w-3.5 h-3.5" style={{ color: batchMode ? 'var(--c-gold)' : 'var(--c-text-muted)' }} />
          <span className="text-[11.5px] font-medium" style={{ color: batchMode ? 'var(--c-gold)' : 'var(--c-text-2)' }}>
            {batchMode ? `Multi-lot · ${selectedLotIds.size} sélectionné(s)` : 'Lot unique'}
          </span>
          <span className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
            {batchMode ? '— lance N rendus en 1 clic' : '— mode classique'}
          </span>
        </div>
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="project-gen-batch-toggle-wrapper">
          <input
            type="checkbox"
            checked={batchMode}
            onChange={(e) => {
              setBatchMode(e.target.checked);
              if (!e.target.checked) setSelectedLotIds(new Set());
            }}
            className="accent-amber-500 w-3.5 h-3.5"
            data-testid="project-gen-batch-toggle"
          />
          <span className="text-[10.5px] uppercase tracking-wider" style={{ color: 'var(--c-text-muted)' }}>
            Multi-lot
          </span>
        </label>
      </div>
      )}

      {/* V14.86 — Maison : batch complet tous étages + rapport de cohérence */}
      {projectTypeCode === 'house_single' && (
        <MaisonBatchLauncher
          projectId={projectId}
          buildParams={buildParams}
          onJobLaunched={(data) => {
            onJobLaunched?.(data);
            setTimeout(refreshLotGenCounts, 10000);
            setTimeout(refreshLotGenCounts, 60000);
          }}
        />
      )}

      {/* Lot picker */}
      <div className="mt-3 space-y-3">
        <div>
          <Label className="text-[11.5px] uppercase tracking-wider" style={{ color: 'var(--c-text-muted)' }}>
            {batchMode ? `Lots à générer (${selectedLotIds.size}/${lots.length})` : 'Lot à générer'}
          </Label>
          {loadingLots ? (
            <div className="mt-1 flex items-center gap-2 text-[12px]" style={{ color: 'var(--c-text-muted)' }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…
            </div>
          ) : batchMode ? (
            <BatchLotPicker
              lots={lots}
              selectedIds={selectedLotIds}
              onChange={setSelectedLotIds}
              filter={batchFilter}
              onFilterChange={setBatchFilter}
              lotGenCounts={lotGenCounts}
              lockStatus={lockStatus}
            />
          ) : (
            <Select value={selectedLotId} onValueChange={setSelectedLotId}>
              <SelectTrigger data-testid="project-gen-lot-select" className="mt-1 bg-white/[0.04] border-white/10 text-white">
                <SelectValue placeholder="— Choisis un lot —" />
              </SelectTrigger>
              <SelectContent>
                {lots.map((l) => (
                  <SelectItem key={l.lot_id} value={l.lot_id}>
                    <span className="inline-flex items-center gap-1.5">
                      <span>
                        {l.lot_number || l.name} · {l.typology}
                        {l.selected_plan_file_id ? ' · plan ✓' : ' · sans plan'}
                      </span>
                      <CorrespondanceBadge
                        correspondance={l.correspondance}
                        showUnique
                        size="xs"
                        testid={`lot-select-corr-${l.lot_id}`}
                      />
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!batchMode && selectedLot && (
            <div className="mt-1.5 flex items-center gap-1.5" data-testid="project-gen-selected-lot-corr">
              <span className="text-[10.5px] uppercase tracking-wider" style={{ color: 'var(--c-text-faint)' }}>
                Correspondance
              </span>
              <CorrespondanceBadge correspondance={selectedLot.correspondance} showUnique size="sm" />
            </div>
          )}
          {!batchMode && selectedLot && !planAttached && (
            <p className="text-[11px] mt-1 text-amber-400">
              Ce lot n'a pas encore de plan retenu. Sélectionne-le dans l'onglet "Fichiers".
            </p>
          )}
          {!batchMode && selectedLot && planAttached && (
            <BindingFileBadge fileId={selectedLot.selected_plan_file_id} lotId={selectedLot.lot_id} />
          )}
        </div>

        {/* Mini timeline — derniers rendus pour ce lot (uniquement en mode lot unique) */}
        {!batchMode && selectedLotId && (
          <LotRenderTimeline
            lotId={selectedLotId}
            history={lotHistory}
            onRefresh={refreshHistory}
            correspondance={selectedLot?.correspondance}
          />
        )}

        {/* V14.85 — Vue config du prompt chargé — tiroir Avancé (V15.11) */}
        {advancedOpen && paramsPreview && (
          <PromptConfigPanel
            projectId={projectId}
            lotId={batchMode ? Array.from(selectedLotIds)[0] : selectedLotId}
            params={buildParams()}
            onParamChange={(key, val) => setCustomParams((p) => ({ ...(p || {}), [key]: val }))}
            onResetParam={(key) => setCustomParams((p) => {
              const next = { ...(p || {}) };
              if (paramsPreview && paramsPreview[key] !== undefined) next[key] = paramsPreview[key]; else delete next[key];
              return next;
            })}
            onOpenFullPrompt={openPromptPreview}
            onLoaded={setPromptMeta}
            onSavedConfig={(values) => setParamsPreview((p) => ({ ...(p || {}), ...values }))}
          >
            <div className="grid grid-cols-[1fr_auto] gap-3 items-start pt-1">
              <div className="space-y-2">
                {/* V15.11 — un seul « Style » : la Matériauthèque quand elle existe, sinon l'ambiance texte libre. */}
                <StyleSummary projectId={projectId} hasMaterialtheque={!!promptMeta?.materials_brief_present}
                              ambiance={(customParams && customParams.style_design) || paramsPreview?.style_design}
                              onAmbianceChange={(v) => setCustomParams((p) => ({ ...(p || {}), style_design: v }))} />
                {customParams?.render_stage === 'whitebox' ? (
                  <div
                    className="flex items-start gap-2 text-[11px] px-2 py-1.5 rounded"
                    style={{ background: 'rgba(232,241,248,0.08)', border: '1px solid rgba(232,241,248,0.3)', color: '#E5E7EB' }}
                    data-testid="project-gen-whitebox-deterministic-hint"
                  >
                    <span>
                      <strong>Étape 1 sans IA</strong> — la maquette blanche est extrudée mathématiquement depuis le tracé
                      (murs, HSP, ouvertures, escaliers, cloisons basses), caméra iso 45° : géométrie exacte, aucun masque ni prompt envoyés à l'IA.
                      L'IA n'intervient qu'aux étapes 2-4 sur cette maquette.
                    </span>
                  </div>
                ) : (
                <label
                  className="flex items-start gap-2 text-[11px] px-2 py-1.5 rounded cursor-pointer"
                  style={{ background: 'rgba(217,160,91,0.08)', border: '1px solid rgba(217,160,91,0.3)', color: '#E5E7EB' }}
                  data-testid="project-gen-mask-projected"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-[#D9A05B]"
                    checked={customParams?.mask_projected === true}
                    onChange={(e) => setCustomParams((c) => {
                      const next = { ...(c || {}) };
                      if (e.target.checked) next.mask_projected = true; else delete next.mask_projected;
                      return next;
                    })}
                    data-testid="project-gen-mask-projected-input"
                  />
                  <span>
                    <strong>Masque projeté (caméra)</strong> — <em>rendu complet : désactivé par défaut</em> :
                    masque pivoté/aplati en losange avant envoi (vues iso).
                  </span>
                </label>
                )}
                {((customParams && customParams.moment_journee) || paramsPreview?.moment_journee || '').toLowerCase().startsWith('midi (sans ombre') && (
                  <div className="flex items-start gap-1.5 text-[10.5px] px-1.5 py-1 rounded"
                       style={{ background: 'rgba(250,204,21,0.10)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.30)' }}
                       data-testid="project-gen-shadowless-hint">
                    <Sparkles className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>Mode <strong>plein midi sans ombre</strong> : éclairage diffus type catalogue 3D. Ombres directionnelles désactivées.</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`/projects/${projectId}/prompt-config`)}
                  className="text-[10.5px] underline hover:opacity-80"
                  style={{ color: 'var(--c-text-muted)' }}
                  data-testid="project-gen-edit-project-config"
                >
                  Éditer le template complet (Config Prompt) →
                </button>
              </div>
              <CameraPosePreview view={(customParams && customParams.vue_angle) || paramsPreview.vue_angle || 'Aérienne isométrique 45°'} />
            </div>
          </PromptConfigPanel>
        )}

        <div className="flex flex-col gap-2 pt-1">
          {/* V14.86 — POC séquençage : étape 1 maquette blanche vs rendu complet */}
          <div
            className="flex items-center gap-1 rounded-lg p-0.5 self-start flex-wrap"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
            role="tablist"
            aria-label="Étape de rendu"
            data-testid="project-gen-stage-tabs"
          >
            {[
              { id: 'whitebox', label: '1 · Maquette blanche', hint: 'Étape 1/4 — maquette blanche DÉTERMINISTE : murs extrudés mathématiquement depuis le tracé (sans IA, 0 crédit, fidélité 100 %). Ancrée comme référence pour les étapes 2-4.' },
              { id: 'textured', label: '2 · Texturer', hint: promptMeta?.whitebox_reference_job_id
                ? 'Étape 2/4 — applique uniquement les matériaux de la Matériauthèque sur la maquette blanche de référence du lot (caméra figée).'
                : 'Étape 2/4 — nécessite d\'abord une maquette blanche ancrée comme référence du lot (galerie → ancre).',
                disabled: !promptMeta?.whitebox_reference_job_id },
              { id: 'furnished', label: '3 · Meubler', hint: promptMeta?.textured_reference_job_id
                ? 'Étape 3/4 — pose uniquement le mobilier retenu dans la Matériauthèque sur le rendu texturé de référence du lot (géométrie, matériaux et caméra figés).'
                : 'Étape 3/4 — nécessite d\'abord un rendu texturé ancré comme référence du lot (galerie → ancre).',
                disabled: !promptMeta?.textured_reference_job_id },
              { id: 'final', label: '4 · Lumière & post-prod', hint: promptMeta?.furnished_reference_job_id
                ? 'Étape 4/4 — applique heure du jour, ombres, ambiance et post-production sur le rendu meublé de référence, sans toucher au reste.'
                : 'Étape 4/4 — nécessite d\'abord un rendu meublé validé (ancré) sur ce lot.',
                disabled: !promptMeta?.furnished_reference_job_id },
              { id: 'full', label: 'Rendu complet', hint: 'Prompt complet : matériaux, mobilier, lumière (une passe).' },
            ].filter((s) => s.id !== 'full' || advancedOpen).map((s) => {
              const cur = customParams?.render_stage || 'full';
              const active = cur === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  disabled={!!s.disabled}
                  title={s.hint}
                  onClick={() => setCustomParams((c) => {
                    const next = { ...(c || {}) };
                    if (s.id === 'full') delete next.render_stage; else next.render_stage = s.id;
                    return next;
                  })}
                  className="text-[11px] px-2.5 py-1 rounded-md transition-colors disabled:opacity-35 disabled:cursor-not-allowed whitespace-nowrap"
                  style={active
                    ? { background: 'rgba(255,255,255,0.92)', color: '#0F1115', fontWeight: 600 }
                    : { color: 'var(--c-text-muted)' }}
                  data-testid={`project-gen-stage-${s.id}`}
                >
                  {s.label}
                </button>
              );
            })}
            <span className="text-[10px] px-2" style={{ color: 'var(--c-text-faint)' }}>
              {customParams?.render_stage === 'whitebox' ? 'Extrusion mathématique du tracé (sans IA) — caméra iso 45°, ancrage automatique'
                : customParams?.render_stage === 'textured' ? `Matériaux seuls sur la maquette de référence${promptMeta?.whitebox_reference_vue_angle ? ` · ${promptMeta.whitebox_reference_vue_angle}` : ''}`
                : customParams?.render_stage === 'furnished' ? 'Mobilier retenu dans la Matériauthèque, posé sur le rendu texturé de référence'
                : customParams?.render_stage === 'final' ? `Lumière « ${customParams?.moment_journee || paramsPreview?.moment_journee || 'Golden Hour'} » + post-prod sur le rendu meublé de référence`
                : 'Une seule passe IA'}
            </span>
          </div>
          {/* V15.11 — réglages propres à l'étape (le reste est dans « Avancé ») */}
          {!advancedOpen && paramsPreview && (
            <div className="space-y-2" data-testid="project-gen-stage-settings">
              {customParams?.render_stage === 'whitebox' && (
                <div className="flex items-start gap-2 text-[11px] px-2 py-1.5 rounded"
                     style={{ background: 'rgba(232,241,248,0.08)', border: '1px solid rgba(232,241,248,0.3)', color: '#E5E7EB' }}
                     data-testid="project-gen-whitebox-deterministic-hint">
                  <span><strong>Étape 1 sans IA</strong> — extrusion mathématique du tracé (murs, HSP, ouvertures, escaliers, éléments fixes), caméra iso 45°, 0 crédit. Aucun réglage : la géométrie vient de l&apos;Éditeur Plan.</span>
                </div>
              )}
              {['textured', 'furnished'].includes(customParams?.render_stage) && (
                <StyleSummary projectId={projectId} hasMaterialtheque={!!promptMeta?.materials_brief_present}
                              ambiance={(customParams && customParams.style_design) || paramsPreview?.style_design}
                              onAmbianceChange={(v) => setCustomParams((p) => ({ ...(p || {}), style_design: v }))} />
              )}
              {customParams?.render_stage === 'final' && (
                <div>
                  <Label className="text-[10.5px] uppercase tracking-wider" style={{ color: 'var(--c-text-faint)' }}>Moment de la journée</Label>
                  <Select value={(customParams && customParams.moment_journee) || paramsPreview?.moment_journee || 'Golden Hour'}
                          onValueChange={(v) => setCustomParams((p) => ({ ...(p || {}), moment_journee: v }))}>
                    <SelectTrigger className="mt-0.5 bg-white/[0.04] border-white/10 text-white h-8 text-[12px]" data-testid="project-gen-moment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Matin', 'Midi', 'Midi (sans ombre portée)', 'Golden Hour', 'Nuit'].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          {/* Sprint 28C — Banner listing which params will use smart defaults.
              Hidden when 0 fields are defaulted. Reads the merged params
              (paramsPreview + customParams overrides) so the warning stays
              accurate as the user tweaks the "Personnaliser" drawer. */}
          {advancedOpen && <TypologyDefaultsWarning
            params={{ ...(paramsPreview || {}), ...(customParams || {}) }}
            projectType={projectTypeCode
              || ((customParams && customParams.type_bien)
                  || (paramsPreview && paramsPreview.type_bien))}
            onApplyDefaults={(patched) => {
              // Only patch the fields the warning resolved to a default,
              // leaving anything the user has already set untouched. We
              // overlay them into customParams so they take precedence
              // when launching.
              const next = { ...(customParams || {}) };
              for (const [k, v] of Object.entries(patched)) {
                if (!next[k] && !(paramsPreview && paramsPreview[k])) next[k] = v;
              }
              setCustomParams(next);
            }}
            testId="project-gen-typology-warning"
          />}
          <div className="flex items-center gap-2 flex-wrap">
            {!batchMode && customParams?.render_stage === 'whitebox' ? (
              <DeterministicWhiteboxButton
                projectId={projectId}
                lotId={selectedLotId}
                vueAngle="Aérienne isométrique 45°"
                disabled={launching || !selectedLotId}
                onDone={() => { refreshHistory(); onJobLaunched?.(); }}
                testId="project-gen-whitebox-deterministic"
              />
            ) : (
            <HudButton
              variant="primary"
              size="md"
              leftIcon={launching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (batchMode ? <Layers className="w-3.5 h-3.5" /> : <Wand2 className="w-3.5 h-3.5" />)}
              onClick={batchMode ? launchBatch : launchShoebox}
              disabled={launching || (batchMode ? selectedLotIds.size === 0 : !planAttached)}
              data-testid={batchMode ? 'project-gen-launch-batch' : 'project-gen-launch-shoebox'}
            >
              {launching
                ? 'Lancement…'
                : batchMode
                  ? `Lancer ${selectedLotIds.size || ''} génération${selectedLotIds.size > 1 ? 's' : ''}`
                  : 'Lancer la génération'}
            </HudButton>
            )}
            <HudButton
              variant="ghost"
              size="md"
              leftIcon={<Eye className="w-3.5 h-3.5" />}
              onClick={openPromptPreview}
              disabled={batchMode ? selectedLotIds.size === 0 : !selectedLotId}
              title="Voir le prompt exact et le masque qui seront envoyés à l'IA — sans consommer de crédit"
              data-testid="project-gen-preview-prompt"
            >
              Prévisualiser le prompt
            </HudButton>
            <button type="button" onClick={() => setAdvancedOpen((v) => !v)}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border transition-colors"
                    style={{ borderColor: advancedOpen ? 'var(--c-gold)' : 'var(--c-border)', color: advancedOpen ? 'var(--c-gold)' : 'var(--c-text-muted)' }}
                    title="Multi-lot, rendu complet en une passe, masque projeté, test caméra, config complète du prompt"
                    data-testid="project-gen-advanced-toggle" aria-pressed={advancedOpen}>
              <Settings2 className="w-3.5 h-3.5" /> Avancé
            </button>
            {advancedOpen && !batchMode && customParams?.render_stage !== 'whitebox' && (
              <HudButton
                variant="secondary"
                size="md"
                leftIcon={<Camera className="w-3.5 h-3.5" />}
                onClick={launchCameraTest}
                disabled={launching || !planAttached}
                title="5 rendus iso 45° identiques pour juger la constance du cadrage (≈ 5 générations)"
                data-testid="project-gen-camera-test"
              >
                Test caméra ×5
              </HudButton>
            )}
            {advancedOpen && <a
              href={`/generation?project=${projectId}${!batchMode && selectedLotId ? `&lot=${selectedLotId}` : ''}`}
              className="text-[11px] underline hover:opacity-80"
              style={{ color: 'var(--c-text-muted)' }}
              data-testid="project-gen-advanced"
            >
              Mode avancé (studio admin) →
            </a>}
          </div>
          {cameraTestId && (
            <CameraTestGrid projectId={projectId} testId={cameraTestId} onClose={() => setCameraTestId(null)} />
          )}
          {abTestId && (
            <ABProjectionGrid projectId={projectId} testId={abTestId} onClose={() => setAbTestId(null)} />
          )}
        </div>
      </div>
    </HudCard>
    <PromptPreviewDialog
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      loading={previewLoading}
      data={previewData}
    />
    </div>
  );
};

// ---------- Small subcomponents ----------

/**
 * StyleSummary — V15.11 : lecture seule du « style » effectif.
 * Matériauthèque présente → c'est elle (rôles retenus / à arbitrer / slots vides) ; sinon ambiance texte libre.
 */
const StyleSummary = ({ projectId, hasMaterialtheque, ambiance, onAmbianceChange }) => {
  const [report, setReport] = useState(null);
  useEffect(() => {
    if (!hasMaterialtheque || !projectId) { setReport(null); return; }
    axios.get(`${API}/materialtheque/projects/${projectId}/brief-report`, { withCredentials: true })
      .then((r) => setReport(r.data)).catch(() => setReport(null));
  }, [projectId, hasMaterialtheque]);
  return (
    <div data-testid="project-gen-style-summary">
      <Label className="text-[10.5px] uppercase tracking-wider" style={{ color: 'var(--c-text-faint)' }}>Style</Label>
      {hasMaterialtheque ? (
        <div className="mt-0.5 flex items-center gap-2 flex-wrap text-[11.5px] px-2 py-1.5 rounded"
             style={{ background: 'rgba(94,234,212,0.08)', border: '1px solid rgba(94,234,212,0.3)', color: '#E5E7EB' }}>
          <Sparkles className="w-3 h-3 shrink-0" style={{ color: '#5EEAD4' }} />
          <span><strong>Matériauthèque du projet</strong> — seul style envoyé à l&apos;IA.</span>
          {report && (
            <span className="text-[10.5px]" style={{ color: 'var(--c-text-2)' }} data-testid="project-gen-style-counts">
              {report.arbitrated_count} retenu{report.arbitrated_count > 1 ? 's' : ''}
              {report.ambiguous_count > 0 && <> · <span style={{ color: '#FDE68A' }}>{report.ambiguous_count} à arbitrer</span></>}
              {report.empty_count > 0 && <> · <span style={{ color: '#FCA5A5' }}>{report.empty_count} slot{report.empty_count > 1 ? 's' : ''} vide{report.empty_count > 1 ? 's' : ''}</span></>}
            </span>
          )}
          <a href={`/materialtheque?project=${projectId}`} className="ml-auto underline text-[10.5px]" style={{ color: '#5EEAD4' }}
             data-testid="project-gen-style-edit-link">Modifier →</a>
        </div>
      ) : (
        <div className="mt-0.5">
          <Select value={ambiance || 'Contemporain'} onValueChange={onAmbianceChange}>
            <SelectTrigger className="bg-white/[0.04] border-white/10 text-white h-8 text-[12px]" data-testid="project-gen-ambiance">
              <SelectValue placeholder="Ambiance" />
            </SelectTrigger>
            <SelectContent>
              {['Contemporain', 'Scandinave', 'Industriel', 'Minimaliste', 'Contemporain marocain', 'Art Déco', 'Japandi'].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[10.5px]" style={{ color: 'var(--c-text-muted)' }}>
            Aucune Matériauthèque : l&apos;ambiance guide seule l&apos;IA.{' '}
            <a href={`/materialtheque?project=${projectId}`} className="underline" data-testid="project-gen-style-create-link">Créer une Matériauthèque →</a>
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * PromptPreviewDialog — V14.74 : shows the exact prompt string + ControlNet
 * mask that will be sent to the model, without spending a credit.
 */
export const PromptPreviewDialog = ({ open, onOpenChange, loading, data }) => {
  const copyPrompt = () => {
    if (!data?.prompt) return;
    navigator.clipboard.writeText(data.prompt)
      .then(() => toast.success('Prompt copié dans le presse-papier'))
      .catch(() => toast.error('Copie impossible'));
  };
  const isLocked = !!data?.locked;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        style={{ background: 'var(--c-surface, #0F1115)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
        data-testid="prompt-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Eye className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
            Prévisualisation du prompt
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--c-text-muted)' }}>
            Prompt exact + masque envoyés au modèle — aucun crédit consommé.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[13px]" style={{ color: 'var(--c-text-muted)' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Construction du prompt…
          </div>
        ) : data ? (
          <div className="space-y-3">
            {/* Mode + stats badges */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="prompt-preview-badges">
              <span
                className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"
                style={isLocked
                  ? { background: 'rgba(14,165,233,0.12)', color: '#38BDF8', border: '1px solid rgba(14,165,233,0.35)' }
                  : { background: 'rgba(148,163,184,0.12)', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.30)' }}
                data-testid="prompt-preview-mode"
              >
                <Lock className="w-3 h-3" />
                {isLocked ? 'Géométrie verrouillée (ControlNet)' : 'Mode legacy (descriptif)'}
              </span>
              {typeof data.walls_count === 'number' && data.walls_count > 0 && (
                <span className="text-[10.5px] px-2 py-1 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--c-text-2)', border: '1px solid var(--c-border)' }}>
                  {data.walls_count} mur{data.walls_count > 1 ? 's' : ''}
                </span>
              )}
              {data.floor_level !== null && data.floor_level !== undefined && (
                <span className="text-[10.5px] px-2 py-1 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--c-text-2)', border: '1px solid var(--c-border)' }}>
                  Niveau {data.floor_level}
                </span>
              )}
              <span className="text-[10.5px] px-2 py-1 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--c-text-2)', border: '1px solid var(--c-border)' }}>
                {data.prompt_chars} caractères
              </span>
              {data.materials_brief_present && (
                <span className="text-[10.5px] px-2 py-1 rounded-full"
                      style={{ background: 'rgba(94,234,212,0.08)', color: '#5EEAD4', border: '1px solid rgba(94,234,212,0.25)' }}>
                  Matériauthèque incluse
                </span>
              )}
            </div>

            {/* ControlNet mask preview */}
            {data.mask_png_b64 && (
              <div>
                <span className="hud-eyebrow">Masque ControlNet (image de référence)</span>
                <div className="mt-1 rounded-lg overflow-hidden inline-block"
                     style={{ border: '1px solid var(--c-border)', background: '#000' }}>
                  <img
                    src={`data:image/png;base64,${data.mask_png_b64}`}
                    alt="Masque ControlNet"
                    className="max-h-56 w-auto"
                    data-testid="prompt-preview-mask"
                  />
                </div>
              </div>
            )}

            {/* Prompt text — V14.85 : blocs sourcés + variables surlignées */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="hud-eyebrow">Prompt texte {data.blocks?.length ? `· ${data.blocks.length} blocs` : ''}</span>
                <button
                  type="button"
                  onClick={copyPrompt}
                  className="inline-flex items-center gap-1 text-[11px] hover:opacity-80"
                  style={{ color: 'var(--c-gold)' }}
                  data-testid="prompt-preview-copy"
                >
                  <Copy className="w-3 h-3" /> Copier
                </button>
              </div>
              {data.blocks?.length ? (
                <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1" data-testid="prompt-preview-blocks">
                  {data.blocks.map((b, i) => (
                    <div key={`${b.id}-${i}`} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--c-border)' }} data-testid={`prompt-block-${b.id}`}>
                      <div className="flex items-center justify-between gap-2 px-2.5 py-1 text-[10.5px]" style={{ background: 'rgba(255,255,255,0.04)' }}>
                        <span className="font-semibold uppercase tracking-wider" style={{ color: 'var(--c-text)' }}>{b.label}</span>
                        <span className="truncate" style={{ color: 'var(--c-text-faint)' }} title={b.edit_hint ? `Modifier via ${b.edit_hint}` : ''}>
                          {b.source}{b.edit_hint ? ` → ${b.edit_hint}` : ''}
                        </span>
                      </div>
                      <pre className="text-[11.5px] leading-relaxed whitespace-pre-wrap p-2.5 font-mono"
                           style={{ background: 'rgba(0,0,0,0.35)', color: 'var(--c-text-2)' }}>
                        <HighlightedText text={b.text} variables={data.variables || []} />
                      </pre>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5 flex-wrap pt-1 text-[10px]" style={{ color: 'var(--c-text-faint)' }} data-testid="prompt-preview-legend">
                    Légende :
                    {Object.keys(ORIGIN_STYLE).map((o) => <OriginChip key={o} origin={o} small />)}
                  </div>
                </div>
              ) : (
                <pre
                  className="text-[11.5px] leading-relaxed whitespace-pre-wrap rounded-lg p-3 max-h-72 overflow-y-auto font-mono"
                  style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}
                  data-testid="prompt-preview-text"
                >
                  {data.prompt}
                </pre>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

/** Surligne dans un bloc de prompt les valeurs des variables (couleur = origine). */
const HighlightedText = ({ text, variables }) => {
  const vars = (variables || [])
    .filter((v) => typeof v.value === 'string' ? v.value.trim().length >= 2 : v.value !== '' && v.value !== null && v.value !== undefined)
    .map((v) => ({ ...v, needle: String(v.value).trim() }))
    .sort((a, b) => b.needle.length - a.needle.length);
  const parts = [];
  let rest = text || '';
  let guard = 0;
  while (rest && guard++ < 400) {
    let best = null;
    for (const v of vars) {
      const idx = rest.indexOf(v.needle);
      if (idx >= 0 && (!best || idx < best.idx)) best = { idx, v };
    }
    if (!best) { parts.push(rest); break; }
    if (best.idx > 0) parts.push(rest.slice(0, best.idx));
    const s = ORIGIN_STYLE[best.v.origin] || ORIGIN_STYLE.defaut;
    parts.push(
      <mark key={parts.length} title={`${best.v.label} · ${best.v.origin_label}`} data-testid={`prompt-highlight-${best.v.key}`}
            style={{ background: s.bg, color: s.fg, borderBottom: `1px solid ${s.fg}`, borderRadius: 2, padding: '0 1px' }}>
        {best.v.needle}
      </mark>,
    );
    rest = rest.slice(best.idx + best.v.needle.length);
  }
  return <>{parts}</>;
};

const ModeTabs = ({ mode, setMode }) => (
  <div
    className="grid grid-cols-2 rounded-lg overflow-hidden mt-1"
    style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
    role="tablist"
    aria-label="Mode de génération"
  >
    {MODES.map((m) => {
      const Icon = m.icon;
      const active = mode === m.key;
      return (
        <button
          key={m.key}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => setMode(m.key)}
          data-testid={`project-gen-mode-${m.key}`}
          className="flex flex-col items-center justify-center py-2 text-[12px] font-semibold tracking-wide transition-colors"
          style={{
            background: active ? 'rgba(217,160,91,0.16)' : 'transparent',
            color: active ? 'var(--c-gold)' : 'var(--c-text-muted)',
            borderRight: m.key === 'shoebox' ? '1px solid var(--c-border)' : 'none',
          }}
        >
          <div className="flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5" />
            {m.label}
          </div>
          <span className="text-[10px] font-normal mt-0.5" style={{ color: 'var(--c-text-faint)' }}>{m.sub}</span>
        </button>
      );
    })}
  </div>
);


/**
 * LotRenderTimeline — last 6 renders for the current lot.
 * Pending/processing jobs show a pulsing tile; completed jobs link to /api/lots/{id}/generations/{job}/image.
 * Clicking a thumbnail opens the image full-size in a new tab so the PM can
 * inspect the latest pass without leaving the studio.
 */
const LotRenderTimeline = ({ lotId, history, onRefresh, correspondance }) => {
  const items = (history || []).slice(0, 6);
  const corr = (correspondance || 'Unique').trim();
  const isSimilar = corr && corr.toLowerCase() !== 'unique' && corr.toLowerCase() !== 'u';
  return (
    <div className="rounded-lg px-2.5 py-2"
         style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)' }}
         data-testid="lot-render-timeline">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <History className="w-3 h-3" style={{ color: 'var(--c-text-muted)' }} />
          <span className="hud-eyebrow">Derniers rendus de ce lot</span>
          <CorrespondanceBadge correspondance={correspondance} showUnique size="xs" testid="lot-timeline-corr" />
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="text-[10.5px] inline-flex items-center gap-1 hover:opacity-80"
          style={{ color: 'var(--c-text-muted)' }}
          aria-label="Rafraîchir l'historique du lot"
          data-testid="lot-timeline-refresh"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
      {isSimilar && (
        <p
          className="text-[10.5px] mb-1.5 px-1.5 py-1 rounded"
          style={{ background: 'rgba(56,189,248,0.08)', color: '#0369A1', border: '1px solid rgba(56,189,248,0.25)' }}
          data-testid="lot-timeline-similar-notice"
        >
          Lot similaire au lot <strong>{corr}</strong> — pas besoin de générer un nouveau shoebox, réutilise les rendus de {corr}.
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-[11px]" style={{ color: 'var(--c-text-faint)' }}>
          Aucun rendu encore. Lance la première génération ci-dessous.
        </p>
      ) : (
        <div className="flex items-stretch gap-1.5 overflow-x-auto pb-0.5" data-testid="lot-timeline-list">
          {items.map((j) => (
            <TimelineTile key={j.job_id} job={j} lotId={lotId} />
          ))}
        </div>
      )}
    </div>
  );
};

const TimelineTile = ({ job, lotId }) => {
  const isDone = job.status === 'completed' && !!job.generated_image_path;
  const isFps  = job.view_type === 'fps';
  const tone = job.status === 'failed' ? '#EF4444'
            : job.status === 'completed' ? '#22C55E'
            : 'var(--c-gold)';
  const url = isDone ? `${API}/lots/${lotId}/generations/${job.job_id}/image` : null;
  const created = job.created_at ? new Date(job.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  const title = `${isFps ? 'FPS' : 'Shoebox'} · ${job.status}${created ? ` · ${created}` : ''}`;
  return (
    <a
      href={url || '#'}
      onClick={(e) => { if (!url) e.preventDefault(); }}
      target={url ? '_blank' : undefined}
      rel="noreferrer"
      title={title}
      data-testid={`lot-timeline-tile-${job.job_id}`}
      className="relative w-12 h-12 rounded shrink-0 overflow-hidden block"
      style={{
        background: 'rgba(0,0,0,0.4)',
        border: `1px solid ${job.status === 'completed' ? 'rgba(34,197,94,0.45)' : 'var(--c-border)'}`,
        cursor: url ? 'pointer' : 'default',
      }}
    >
      {isDone ? (
        <img src={url} alt="rendu" className="w-full h-full object-cover" crossOrigin="use-credentials" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {job.status === 'failed' ? (
            <span className="text-[9px] font-bold" style={{ color: tone }}>×</span>
          ) : (
            <Loader2 className="w-3 h-3 animate-spin" style={{ color: tone }} />
          )}
        </div>
      )}
      {/* badge mode */}
      <span
        className="absolute top-0.5 left-0.5 text-[7.5px] font-bold uppercase px-0.5 rounded leading-[10px]"
        style={{
          background: isFps ? 'rgba(217,160,91,0.85)' : 'rgba(0,0,0,0.70)',
          color: isFps ? '#000' : '#fff',
        }}
      >
        {isFps ? 'FPS' : 'SB'}
      </span>
      {/* status dot */}
      <span
        className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full"
        style={{ background: tone, boxShadow: `0 0 4px ${tone}` }}
      />
    </a>
  );
};

const BindingFileBadge = ({ fileId, lotId }) => {
  const [file, setFile] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setFile(null);
    if (!fileId) return undefined;
    axios.get(`${API}/files/${fileId}/meta`, { withCredentials: true })
      .then((r) => { if (!cancelled) setFile(r.data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [fileId]);
  if (!file) return null;
  const isMask = file.file_kind === 'controlnet_mask';
  const mismatch = isMask && file.lot_id && file.lot_id !== lotId;
  return (
    <p className="text-[11px] mt-1 flex items-center gap-1.5 flex-wrap" style={{ color: mismatch ? '#FCA5A5' : '#9CA3AF' }} data-testid="project-gen-binding-file">
      <span className="px-1.5 py-0.5 rounded" style={{ background: isMask ? 'rgba(217,160,91,0.15)' : 'rgba(255,255,255,0.06)', color: isMask ? '#FBBF24' : '#D1D5DB' }}>
        {isMask ? 'Masque ControlNet' : 'Plan'}
      </span>
      <span className="truncate max-w-[260px]" title={file.original_filename}>{file.original_filename}</span>
      {file.lot_label && <span className="opacity-70">· lot {file.lot_label}</span>}
      {mismatch && <span>⚠ appartient à un autre lot</span>}
    </p>
  );
};





/**
 * CameraPosePreview — Sprint #8 mini diagram showing the exact azimuth /
 * elevation / projection that Nano Banana will receive as the LAST reference
 * image. Empowers the user to spot the difference between "Aérienne
 * isométrique 45°" (apartment rotated 45° around Y) and "Plongée 45°"
 * (camera tilted 45° but apartment axis-aligned) at a glance.
 */
const CameraPosePreview = ({ view }) => {
  const src = `${API}/generation/camera-reference.png?view=${encodeURIComponent(view || 'Aérienne isométrique 45°')}`;
  return (
    <div
      className="rounded-md p-1.5 w-[112px]"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--c-border)' }}
      data-testid="project-gen-camera-preview"
      title={`Aperçu de la pose caméra envoyée à l'IA (${view})`}
    >
      <img
        src={src}
        alt={`Pose caméra ${view}`}
        className="w-full h-auto rounded"
        loading="lazy"
      />
      <p className="text-[9px] text-center mt-1 uppercase tracking-wider"
         style={{ color: 'var(--c-text-faint)' }}>
        ↑ pose IA
      </p>
    </div>
  );
};


/**
 * BatchLotPicker — V11.5 multi-lot checklist.
 *
 * Lets the PM tick several lots at once and dispatch all of them in a single
 * batch call. Surfaces the same correspondance / plan / generation-count pills
 * as the admin Studio so the user instantly sees which lots are still missing
 * a shoebox vs which already have one. The "Uniques" quick action solves the
 * primary request: "select all unique lots in one click".
 *
 * Note: the dark HUD theme reuses the launcher's surface/border CSS vars so the
 * checklist visually merges with the surrounding HudCard.
 */
const BatchLotPicker = ({ lots, selectedIds, onChange, filter, onFilterChange, lotGenCounts, lockStatus }) => {
  const isUniqueCorr = (c) => {
    const v = (c || 'Unique').trim().toLowerCase();
    return !v || v === 'unique' || v === 'u';
  };

  const filtered = useMemo(() => {
    const q = (filter || '').trim().toLowerCase();
    if (!q) return lots;
    return lots.filter((l) =>
      (l.lot_number || '').toLowerCase().includes(q)
      || (l.name || '').toLowerCase().includes(q)
      || (l.typology || '').toLowerCase().includes(q),
    );
  }, [lots, filter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((l) => selectedIds.has(l.lot_id));

  const toggle = (lotId) => {
    const next = new Set(selectedIds);
    if (next.has(lotId)) next.delete(lotId);
    else next.add(lotId);
    onChange(next);
  };

  const setSelection = (predicate) => {
    const next = new Set(selectedIds);
    filtered.forEach((l) => {
      if (predicate(l)) next.add(l.lot_id);
      else next.delete(l.lot_id);
    });
    onChange(next);
  };

  const selectAllFiltered = () => {
    const next = new Set(selectedIds);
    filtered.forEach((l) => next.add(l.lot_id));
    onChange(next);
  };
  const deselectAllFiltered = () => {
    const next = new Set(selectedIds);
    filtered.forEach((l) => next.delete(l.lot_id));
    onChange(next);
  };

  const selectUniques = () => setSelection((l) => isUniqueCorr(l.correspondance));
  const selectUngenerated = () => setSelection((l) => !(lotGenCounts?.[l.lot_id] > 0));

  if (lots.length === 0) {
    return (
      <p className="text-[11px] italic mt-2" style={{ color: 'var(--c-text-faint)' }}>
        Aucun lot dans ce projet — importe ton XLS pour les créer.
      </p>
    );
  }

  return (
    <div className="mt-1 space-y-1.5" data-testid="project-gen-batch-picker">
      {/* Toolbar: filter + quick actions */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <input
          type="text"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          placeholder="Filtrer (n° lot, typologie…)"
          className="h-7 text-[11.5px] px-2 rounded flex-1 min-w-[140px] bg-white/[0.04] border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/60"
          data-testid="project-gen-batch-filter"
        />
        <button
          type="button"
          onClick={allFilteredSelected ? deselectAllFiltered : selectAllFiltered}
          className="h-7 px-2 text-[10.5px] rounded font-medium uppercase tracking-wider transition-colors"
          style={{
            background: allFilteredSelected ? 'rgba(255,255,255,0.04)' : 'rgba(217,160,91,0.16)',
            color: allFilteredSelected ? 'var(--c-text-muted)' : 'var(--c-gold)',
            border: '1px solid rgba(217,160,91,0.30)',
          }}
          data-testid="project-gen-batch-select-all"
        >
          {allFilteredSelected ? 'Aucun' : `Tous (${filtered.length})`}
        </button>
        <button
          type="button"
          onClick={selectUniques}
          className="h-7 px-2 text-[10.5px] rounded font-medium uppercase tracking-wider transition-colors"
          style={{
            background: 'rgba(16,185,129,0.10)',
            color: '#10B981',
            border: '1px solid rgba(16,185,129,0.30)',
          }}
          title="Cocher tous les lots Uniques (les lots Similaires partagent déjà le shoebox de leur lot de référence)"
          data-testid="project-gen-batch-select-uniques"
        >
          Uniques
        </button>
        <button
          type="button"
          onClick={selectUngenerated}
          className="h-7 px-2 text-[10.5px] rounded font-medium uppercase tracking-wider transition-colors"
          style={{
            background: 'rgba(217,160,91,0.10)',
            color: 'var(--c-gold)',
            border: '1px solid rgba(217,160,91,0.30)',
          }}
          title="Cocher uniquement les lots qui n'ont pas encore de Shoebox généré"
          data-testid="project-gen-batch-select-ungenerated"
        >
          Non générés
        </button>
      </div>

      {/* Lot list — scrollable checklist */}
      <div
        className="max-h-56 overflow-y-auto rounded-md divide-y divide-white/[0.04]"
        style={{ background: 'rgba(0,0,0,0.18)', border: '1px solid var(--c-border)' }}
        data-testid="project-gen-batch-list"
      >
        {filtered.length === 0 ? (
          <p className="text-[11px] italic p-2.5" style={{ color: 'var(--c-text-faint)' }}>
            Aucun lot ne correspond au filtre.
          </p>
        ) : (
          filtered.map((l) => {
            const checked = selectedIds.has(l.lot_id);
            const lock = lockStatus?.[l.lot_id];
            const isLocked = !!lock?.locked;
            const hasPlan = !!l.selected_plan_file_id || isLocked;
            const genCount = lotGenCounts?.[l.lot_id] || 0;
            return (
              <label
                key={l.lot_id}
                className="flex items-center gap-2 px-2 py-1.5 cursor-pointer text-[11.5px] transition-colors"
                style={{ background: checked ? 'rgba(217,160,91,0.12)' : 'transparent' }}
                data-testid={`project-gen-batch-item-${l.lot_id}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(l.lot_id)}
                  className="accent-amber-500 w-3.5 h-3.5 shrink-0"
                  data-testid={`project-gen-batch-checkbox-${l.lot_id}`}
                />
                <span className="font-medium shrink-0" style={{ color: 'var(--c-text)' }}>
                  {l.lot_number || l.name}
                </span>
                <span className="shrink-0" style={{ color: 'var(--c-text-faint)' }}>·</span>
                <span className="flex-1 truncate" style={{ color: 'var(--c-text-2)' }} title={l.name}>
                  {l.typology}
                  {l.name && l.name !== l.lot_number ? ` — ${l.name}` : ''}
                </span>
                {isLocked && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold uppercase tracking-wider px-1 py-0.5 rounded-full shrink-0"
                    style={{ background: 'rgba(14,165,233,0.12)', color: '#38BDF8', border: '1px solid rgba(14,165,233,0.35)' }}
                    title={`Géométrie verrouillée — génère depuis le masque ControlNet tracé (${lock.walls_count} mur${lock.walls_count > 1 ? 's' : ''}${lock.calibrated ? ' · échelle calibrée' : ' · ⚠ non calibré'})`}
                    data-testid={`project-gen-batch-lock-${l.lot_id}`}
                  >
                    <Lock className="w-2.5 h-2.5" />
                    verrouillé
                  </span>
                )}
                {!hasPlan && (
                  <span
                    className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider px-1 py-0.5 rounded-full shrink-0"
                    style={{ background: 'rgba(251,146,60,0.15)', color: '#FB923C', border: '1px solid rgba(251,146,60,0.30)' }}
                    title="Ni géométrie tracée ni plan rattaché — la génération sera ignorée pour ce lot"
                  >
                    sans plan
                  </span>
                )}
                {!isLocked && !!l.selected_plan_file_id && (
                  <span
                    className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider px-1 py-0.5 rounded-full shrink-0"
                    style={{ background: 'rgba(148,163,184,0.12)', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.30)' }}
                    title="Pas de géométrie tracée — génération legacy depuis le plan rattaché (moins fidèle). Tracez les murs en 2·Structure pour verrouiller."
                    data-testid={`project-gen-batch-legacy-${l.lot_id}`}
                  >
                    legacy
                  </span>
                )}
                <CorrespondanceBadge
                  correspondance={l.correspondance}
                  showUnique
                  size="xs"
                  testid={`project-gen-batch-corr-${l.lot_id}`}
                />
                {genCount > 0 && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold uppercase tracking-wider px-1 py-0.5 rounded-full shrink-0"
                    style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981', border: '1px solid rgba(16,185,129,0.30)' }}
                    title={`Shoebox déjà généré ${genCount}×`}
                    data-testid={`project-gen-batch-gencount-${l.lot_id}`}
                  >
                    <CheckSquare className="w-2.5 h-2.5" />
                    {genCount}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>

      <p className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
        <Lock className="w-2.5 h-2.5 inline mr-0.5" style={{ color: '#38BDF8' }} />
        <strong style={{ color: '#38BDF8' }}>Verrouillé</strong> = génère depuis le masque ControlNet tracé (fidèle au plan).
        <strong style={{ color: '#94A3B8' }}> Legacy</strong> = plan image simple, moins fidèle.
        Les lots <strong style={{ color: '#10B981' }}>Uniques</strong> génèrent leur propre Shoebox.
        Les lots <strong style={{ color: '#0EA5E9' }}>Similaires</strong> partagent le rendu de leur lot de référence — décocher recommandé.
      </p>
    </div>
  );
};


export default ProjectGenerationLauncher;
